/**
 * Chess Coach AI Service
 * 
 * Uses Google Gemini AI to provide chess move explanations,
 * strategic advice, and position analysis.
 */

import { Move, GameState, PlayerColor } from '../types';
import { ChessNotation } from '../ChessNotation';
import { AWS_CONFIG } from '../../aws/config';

export interface MoveExplanation {
  move: string;
  explanation: string;
  strategy: string;
  alternatives?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export class ChessCoachAI {
  private apiEndpoint: string;
  private useProxy: boolean;
  private apiKey: string;
  private notation: ChessNotation;
  private conversationHistory: ChatMessage[] = [];

  constructor(apiKey?: string) {
    // Use Lambda proxy endpoint if configured, otherwise fall back to direct API
    this.apiEndpoint = AWS_CONFIG.gemini.proxyEndpoint;
    this.useProxy = !!this.apiEndpoint;
    this.apiKey = apiKey || '';
    this.notation = new ChessNotation();
    
    if (!this.useProxy && !this.apiKey) {
      console.warn('ChessCoachAI: No proxy endpoint or API key configured. AI coach will not work.');
    }
    
    if (!this.useProxy && this.apiKey) {
      console.warn('ChessCoachAI: Using direct Gemini API (development mode). Deploy Lambda proxy for production.');
    }
  }

  /**
   * Explain a chess move
   */
  async explainMove(
    move: Move,
    gameState: GameState,
    playerColor: PlayerColor
  ): Promise<MoveExplanation> {
    const moveNotation = move.notation;
    const currentPlayer = gameState.currentPlayer;
    const moveNumber = Math.floor(gameState.moveHistory.length / 2) + 1;
    
    // Build context about the position
    const context = this.buildPositionContext(gameState);
    
    const prompt = `You are a chess coach explaining moves to a student. 

Current position AFTER the move:
${context}

Move ${moveNumber}: ${currentPlayer === 'white' ? 'White' : 'Black'} just played ${moveNotation}

Provide a brief explanation (2-3 sentences) covering:
1. What the move accomplishes (e.g., "develops the knight", "attacks the queen", "controls the center")
2. The strategic purpose (e.g., "prepares castling", "increases pressure on f7", "improves piece coordination")
3. One alternative move if relevant

Keep it concise and educational. Format as JSON:
{
  "explanation": "brief explanation of what the move does",
  "strategy": "strategic purpose",
  "alternatives": "alternative move if relevant, or empty string"
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const parsed = this.parseResponse(response);
      
      return {
        move: moveNotation,
        explanation: parsed.explanation || 'Move played.',
        strategy: parsed.strategy || '',
        alternatives: parsed.alternatives || undefined
      };
    } catch (error) {
      console.error('ChessCoachAI: Error explaining move:', error);
      return {
        move: moveNotation,
        explanation: `${currentPlayer === 'white' ? 'White' : 'Black'} played ${moveNotation}`,
        strategy: 'Developing the position.',
        alternatives: undefined
      };
    }
  }

  /**
   * Answer a user question about the position
   */
  async askQuestion(question: string, gameState: GameState): Promise<string> {
    const context = this.buildPositionContext(gameState);
    
    const prompt = `You are a chess coach. The student asks: "${question}"

${context}

Give a clear, specific answer in 2-3 short sentences. Mention specific pieces and squares.`;

    try {
      const response = await this.callGeminiAPI(prompt);
      
      // Add to conversation history
      this.conversationHistory.push({
        role: 'user',
        content: question,
        timestamp: Date.now()
      });
      this.conversationHistory.push({
        role: 'assistant',
        content: response,
        timestamp: Date.now()
      });
      
      // Keep only last 10 messages
      if (this.conversationHistory.length > 10) {
        this.conversationHistory = this.conversationHistory.slice(-10);
      }
      
      return response;
    } catch (error) {
      console.error('ChessCoachAI: Error answering question:', error);
      return "I'm having trouble connecting right now. Please try again.";
    }
  }

  /**
   * Get position analysis
   */
  async analyzePosition(gameState: GameState): Promise<string> {
    const context = this.buildPositionContext(gameState);
    
    const prompt = `You are a chess coach analyzing a position.

${context}

Provide a brief analysis (3-4 sentences) covering:
1. Material balance and piece activity
2. Key features of the position (pawn structure, king safety, piece placement)
3. General plan for both sides

Be specific about pieces and squares. Be concise and clear.`;

    try {
      return await this.callGeminiAPI(prompt);
    } catch (error) {
      console.error('ChessCoachAI: Error analyzing position:', error);
      return 'Unable to analyze position at this time.';
    }
  }

  /**
   * Build context about the current position
   */
  private buildPositionContext(gameState: GameState): string {
    const moveCount = gameState.moveHistory.length;
    const lastMoves = gameState.moveHistory.slice(-6).map(m => m.notation).join(' ');
    
    let context = `Move ${Math.floor(moveCount / 2) + 1}\n`;
    context += `Current turn: ${gameState.currentPlayer}\n\n`;
    
    // Add the actual board position in FEN-like format
    context += `Board position (from White's perspective, rank 8 to rank 1):\n`;
    context += this.boardToText(gameState.board) + '\n\n';
    
    if (lastMoves) {
      context += `Recent moves: ${lastMoves}\n`;
    }
    
    // Material count
    const whitePieces = gameState.capturedPieces.white.length;
    const blackPieces = gameState.capturedPieces.black.length;
    if (whitePieces > 0 || blackPieces > 0) {
      context += `Captured pieces: White captured ${blackPieces} black pieces, Black captured ${whitePieces} white pieces\n`;
    }
    
    // Game status
    if (gameState.gameStatus.type === 'check') {
      context += `IMPORTANT: ${gameState.gameStatus.player} is in check!\n`;
    }
    
    return context;
  }

  /**
   * Convert board to readable text format
   */
  private boardToText(board: (any | null)[][]): string {
    const pieceSymbols: { [key: string]: string } = {
      'white-king': '♔', 'white-queen': '♕', 'white-rook': '♖',
      'white-bishop': '♗', 'white-knight': '♘', 'white-pawn': '♙',
      'black-king': '♚', 'black-queen': '♛', 'black-rook': '♜',
      'black-bishop': '♝', 'black-knight': '♞', 'black-pawn': '♟'
    };

    let boardText = '  a b c d e f g h\n';
    
    // Iterate from rank 7 (8th rank) down to rank 0 (1st rank)
    for (let rank = 7; rank >= 0; rank--) {
      boardText += `${rank + 1} `;
      for (let file = 0; file < 8; file++) {
        // Access the 2D array directly: board[rank][file]
        const piece = board[rank]?.[file];
        if (piece) {
          const key = `${piece.color}-${piece.type}`;
          boardText += pieceSymbols[key] || '?';
        } else {
          boardText += '·';
        }
        boardText += ' ';
      }
      boardText += `${rank + 1}\n`;
    }
    
    boardText += '  a b c d e f g h';
    
    return boardText;
  }

  /**
   * Call Gemini API via Lambda proxy or direct
   */
  private async callGeminiAPI(prompt: string): Promise<string> {
    if (this.useProxy) {
      return this.callViaProxy(prompt);
    } else {
      return this.callDirectAPI(prompt);
    }
  }

  /**
   * Call via Lambda proxy (production)
   */
  private async callViaProxy(prompt: string): Promise<string> {
    console.log('ChessCoachAI: Calling Gemini proxy...');
    
    const response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        config: {
          temperature: 0.7,
          maxOutputTokens: 2000,
          topP: 0.8,
          topK: 40
        }
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('ChessCoachAI: Proxy Error:', error);
      throw new Error(`Gemini proxy error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.text) {
      throw new Error('Invalid response from Gemini proxy');
    }

    return data.text;
  }

  /**
   * Call Gemini API directly (development only)
   */
  private async callDirectAPI(prompt: string): Promise<string> {
    if (!this.apiKey) {
      throw new Error('No API key available');
    }

    console.log('ChessCoachAI: Calling Gemini API directly (dev mode)...');
    
    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 2000,
          topP: 0.8,
          topK: 40
        }
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('ChessCoachAI: API Error:', error);
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.candidates || !data.candidates[0]?.content?.parts?.[0]?.text) {
      throw new Error('Invalid response from Gemini API');
    }

    return data.candidates[0].content.parts[0].text.trim();
  }

  /**
   * Parse JSON response from AI
   */
  private parseResponse(response: string): any {
    try {
      // Try to extract JSON from markdown code blocks
      const jsonMatch = response.match(/```json\s*([\s\S]*?)\s*```/) || 
                       response.match(/```\s*([\s\S]*?)\s*```/) ||
                       [null, response];
      
      const jsonStr = jsonMatch[1] || response;
      return JSON.parse(jsonStr);
    } catch (error) {
      // If parsing fails, return the raw response
      return {
        explanation: response,
        strategy: '',
        alternatives: ''
      };
    }
  }

  /**
   * Get conversation history
   */
  getConversationHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }
}
